import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon,
  TrophyIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import SearchFilter from '../../components/SearchFilter';
import Calendar from '../../components/Calendar';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    sport: 'all',
    month: 'all'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'

  // Sample events data (will be replaced by API data when available)
  const sampleEvents = [
    {
      id: 1,
      title: 'Eagles vs Hawks - Championship Game',
      description: 'Regional championship game against Central Valley Hawks. Come support our Eagles!',
      eventType: 'game',
      startDate: '2024-02-15T19:00:00Z',
      endDate: '2024-02-15T21:00:00Z',
      location: {
        venue: 'East End Prep Stadium',
        address: '123 Eagle Way, East End, NY 12345'
      },
      sport: {
        id: 1,
        name: 'Football',
        slug: 'football'
      },
      opponent: 'Central Valley Hawks',
      gameResult: null,
      isPublic: true,
      requiresRSVP: false,
      attendeeCount: 0,
      maxAttendees: null,
      status: 'scheduled',
      ticketInfo: {
        required: true,
        price: 5.00,
        availableOnline: true
      }
    },
    {
      id: 2,
      title: 'Girls Basketball vs Riverside Rams',
      description: 'Home game against Riverside Rams. Let\'s pack the gym and cheer on our Lady Eagles!',
      eventType: 'game',
      startDate: '2024-02-12T18:30:00Z',
      endDate: '2024-02-12T20:00:00Z',
      location: {
        venue: 'East End Prep Gymnasium',
        address: '123 Eagle Way, East End, NY 12345'
      },
      sport: {
        id: 4,
        name: 'Girls Basketball',
        slug: 'girls-basketball'
      },
      opponent: 'Riverside Rams',
      gameResult: null,
      isPublic: true,
      requiresRSVP: false,
      attendeeCount: 0,
      maxAttendees: null,
      status: 'scheduled',
      ticketInfo: {
        required: true,
        price: 3.00,
        availableOnline: true
      }
    },
    {
      id: 3,
      title: 'Athletic Awards Banquet',
      description: 'Annual awards ceremony celebrating our student-athletes\' achievements throughout the year.',
      eventType: 'ceremony',
      startDate: '2024-02-20T18:00:00Z',
      endDate: '2024-02-20T21:00:00Z',
      location: {
        venue: 'East End Prep Cafeteria',
        address: '123 Eagle Way, East End, NY 12345'
      },
      sport: null,
      opponent: null,
      gameResult: null,
      isPublic: true,
      requiresRSVP: true,
      attendeeCount: 45,
      maxAttendees: 150,
      status: 'scheduled',
      ticketInfo: {
        required: false,
        price: 0,
        availableOnline: false
      }
    },
    {
      id: 4,
      title: 'Spring Sports Tryouts',
      description: 'Tryouts for Track & Field and Flag Football. All interested students welcome!',
      eventType: 'tryout',
      startDate: '2024-03-01T15:30:00Z',
      endDate: '2024-03-01T17:00:00Z',
      location: {
        venue: 'East End Prep Athletic Fields',
        address: '123 Eagle Way, East End, NY 12345'
      },
      sport: null,
      opponent: null,
      gameResult: null,
      isPublic: true,
      requiresRSVP: true,
      attendeeCount: 12,
      maxAttendees: 50,
      status: 'scheduled',
      ticketInfo: {
        required: false,
        price: 0,
        availableOnline: false
      }
    },
    {
      id: 5,
      title: 'Boys Soccer vs Mountain View Eagles',
      description: 'Away game against Mountain View Eagles. Transportation provided for families.',
      eventType: 'game',
      startDate: '2024-02-18T16:00:00Z',
      endDate: '2024-02-18T17:30:00Z',
      location: {
        venue: 'Mountain View High School',
        address: '456 Mountain Rd, Mountain View, NY 12346'
      },
      sport: {
        id: 3,
        name: 'Boys Soccer',
        slug: 'boys-soccer'
      },
      opponent: 'Mountain View Eagles',
      gameResult: null,
      isPublic: true,
      requiresRSVP: false,
      attendeeCount: 0,
      maxAttendees: null,
      status: 'scheduled',
      ticketInfo: {
        required: true,
        price: 4.00,
        availableOnline: false
      }
    }
  ];

  const eventTypes = [
    { value: 'all', label: 'All Events' },
    { value: 'game', label: 'Games' },
    { value: 'practice', label: 'Practices' },
    { value: 'tournament', label: 'Tournaments' },
    { value: 'ceremony', label: 'Ceremonies' },
    { value: 'tryout', label: 'Tryouts' },
    { value: 'meeting', label: 'Meetings' }
  ];

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'girls-basketball', label: 'Girls Basketball' },
    { value: 'boys-basketball', label: 'Boys Basketball' },
    { value: 'girls-soccer', label: 'Girls Soccer' },
    { value: 'boys-soccer', label: 'Boys Soccer' },
    { value: 'track-field', label: 'Track & Field' }
  ];

  // Generate dynamic months based on available events (current and future only)
  const generateMonthOptions = (eventsList) => {
    const monthSet = new Set();
    const currentDate = new Date();
    const currentMonthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    
    eventsList.forEach(event => {
      const eventDate = new Date(event.startDate);
      const monthKey = `${eventDate.getFullYear()}-${String(eventDate.getMonth() + 1).padStart(2, '0')}`;
      
      // Only include current and future months
      if (monthKey >= currentMonthKey) {
        monthSet.add(monthKey);
      }
    });
    
    const sortedMonths = Array.from(monthSet).sort();
    const monthOptions = [{ value: 'all', label: 'All Months' }];
    
    sortedMonths.forEach(monthKey => {
      const [year, month] = monthKey.split('-');
      const date = new Date(year, month - 1, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      monthOptions.push({ value: monthKey, label: monthName });
    });
    
    return monthOptions;
  };
  
  const months = generateMonthOptions([...events, ...sampleEvents]);

  const filterOptions = {
    type: eventTypes,
    sport: sports,
    month: months
  };

  useEffect(() => {
    fetchEvents();
    fetchUpcomingEvents();
  }, [currentPage, filters, searchTerm]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 12,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.type !== 'all' && { type: filters.type }),
        ...(filters.sport !== 'all' && { sport: filters.sport }),
        ...(filters.month !== 'all' && { month: filters.month })
      });

      const response = await fetch(`${API_BASE_URL}/events?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        // Filter out past events from API response
        const filteredEvents = (data.events || []).filter(event => {
          const eventEndDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
          return eventEndDate >= new Date();
        });
        setEvents(filteredEvents);
        setTotalPages(data.totalPages || 1);
      } else {
        // Use sample data if API is not available
        const filteredEvents = sampleEvents.filter(event => {
          // Filter out past events
          const eventEndDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
          const isPastEvent = eventEndDate < new Date();
          if (isPastEvent) return false;
          
          const matchesSearch = !searchTerm || 
            event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.sport?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            event.opponent?.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesType = filters.type === 'all' || event.eventType === filters.type;
          const matchesSport = filters.sport === 'all' || event.sport?.slug === filters.sport;
          const matchesMonth = filters.month === 'all' || 
            new Date(event.startDate).toISOString().slice(0, 7) === filters.month;
          return matchesSearch && matchesType && matchesSport && matchesMonth;
        });
        
        setEvents(filteredEvents);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      // Filter out past events from sample data
      const filteredSampleEvents = sampleEvents.filter(event => {
        const eventEndDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
        return eventEndDate >= new Date();
      });
      setEvents(filteredSampleEvents);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcomingEvents = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events/upcoming?limit=5`);
      
      if (response.ok) {
        const data = await response.json();
        setUpcomingEvents(data.events || []);
      } else {
        // Use sample upcoming events
        const upcoming = sampleEvents
          .filter(event => new Date(event.startDate) > new Date())
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
          .slice(0, 5);
        setUpcomingEvents(upcoming);
      }
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      const upcoming = sampleEvents
        .filter(event => new Date(event.startDate) > new Date())
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
        .slice(0, 5);
      setUpcomingEvents(upcoming);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'game': return 'bg-blue-100 text-blue-800';
      case 'practice': return 'bg-green-100 text-green-800';
      case 'tournament': return 'bg-purple-100 text-purple-800';
      case 'ceremony': return 'bg-yellow-100 text-yellow-800';
      case 'tryout': return 'bg-orange-100 text-orange-800';
      case 'meeting': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeIcon = (type) => {
    switch (type) {
      case 'game': return TrophyIcon;
      case 'practice': return CalendarIcon;
      case 'tournament': return TrophyIcon;
      case 'ceremony': return CalendarIcon;
      case 'tryout': return CalendarIcon;
      case 'meeting': return CalendarIcon;
      default: return CalendarIcon;
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header Section */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-lg text-white mb-4">Eagles Calendar</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Stay informed about upcoming games, events, and important dates 
            for East End Prep athletics and school activities.
          </p>
        </div>
      </section>

      {/* Upcoming Events Highlight */}
      {upcomingEvents.length > 0 && (
        <section className="bg-white py-12 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-md text-gray-900 mb-8 text-center">Next Up</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.slice(0, 3).map((event) => {
                const IconComponent = getEventTypeIcon(event.eventType);
                return (
                  <div key={event.id} className="card bg-white border-2 border-blue-600">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="badge bg-blue-100 text-blue-600">
                          {event.eventType}
                        </span>
                        <IconComponent className="w-6 h-6 text-blue-600" />
                      </div>
                      
                      <h3 className="text-lg font-bold mb-2 text-blue-600">
                        <Link 
                          to={`/events/${event.id}`}
                          className="hover:text-blue-800 transition-colors duration-200 text-blue-600"
                        >
                          {event.title}
                        </Link>
                      </h3>
                      
                      <div className="space-y-2 text-sm text-blue-600">
                        <div className="flex items-center">
                          <CalendarIcon className="w-4 h-4 mr-2 text-blue-600" />
                          {formatDate(event.startDate)}
                        </div>
                        <div className="flex items-center">
                          <ClockIcon className="w-4 h-4 mr-2 text-blue-600" />
                          {formatTime(event.startDate)}
                        </div>
                        <div className="flex items-center">
                          <MapPinIcon className="w-4 h-4 mr-2 text-blue-600" />
                          {event.location.venue}
                        </div>
                      </div>
                      
                      {event.sport && (
                        <div className="mt-4">
                          <span className="badge bg-blue-100 text-blue-600">
                            {event.sport.name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Search, Filters and View Toggle */}
      <section className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex-1">
              <SearchFilter
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                filters={filters}
                onFilterChange={(newFilters) => {
                  setFilters(newFilters);
                  setCurrentPage(1);
                }}
                filterOptions={filterOptions}
                placeholder="Search events, sports, or opponents..."
              />
            </div>

            {/* View Toggle */}
            <div className="flex items-center space-x-2 lg:ml-6">
              <span className="text-sm font-medium text-gray-700">View:</span>
              <div className="flex rounded-md border border-gray-300">
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1 text-sm font-medium rounded-l-md ${
                    viewMode === 'list'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  List
                </button>
                <button
                  onClick={() => setViewMode('calendar')}
                  className={`px-3 py-1 text-sm font-medium rounded-r-md border-l ${
                    viewMode === 'calendar'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Calendar
                </button>
              </div>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600 text-right">
            Showing {events.length} events
          </div>
        </div>
      </section>

      {/* Content based on view mode */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {viewMode === 'calendar' ? (
            <Calendar 
              events={events} 
              filters={filters}
              onFilterChange={setFilters}
            />
          ) : (
            <>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="spinner spinner-primary w-8 h-8"></div>
                </div>
              ) : events.length > 0 ? (
                <>
                  <div className="grid gap-6">
                    {events.map((event) => {
                      const IconComponent = getEventTypeIcon(event.eventType);
                      return (
                        <div key={event.id} className="card card-hover bg-white border-2 border-gray-200">
                          <div className="p-6">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-3">
                                  <IconComponent className="w-6 h-6 text-blue-600" />
                                  <span className={`badge ${getEventTypeColor(event.eventType)}`}>
                                    {event.eventType}
                                  </span>
                                  {event.sport && (
                                    <span className="badge bg-primary text-white">
                                      {event.sport.name}
                                    </span>
                                  )}
                                  {event.requiresRSVP && (
                                    <span className="badge bg-orange-100 text-orange-800">
                                      RSVP Required
                                    </span>
                                  )}
                                </div>
                                
                                <h3 className="text-xl font-bold text-blue-600 mb-2">
                                  <Link 
                                    to={`/events/${event.id}`}
                                    className="hover:text-blue-800 transition-colors duration-200 text-blue-600"
                                  >
                                    {event.title}
                                  </Link>
                                </h3>
                                
                                {event.opponent && (
                                  <p className="text-lg text-blue-600 mb-2 font-bold">
                                    vs {event.opponent}
                                  </p>
                                )}
                                
                                <p className="text-blue-600 mb-4 font-medium">
                                  {event.description}
                                </p>
                                
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-blue-600 font-medium">
                                  <div className="flex items-center">
                                    <CalendarIcon className="w-4 h-4 mr-2 text-blue-600" />
                                    {formatDate(event.startDate)}
                                  </div>
                                  <div className="flex items-center">
                                    <ClockIcon className="w-4 h-4 mr-2 text-blue-600" />
                                    {formatTime(event.startDate)}
                                  </div>
                                  <div className="flex items-center">
                                    <MapPinIcon className="w-4 h-4 mr-2 text-blue-600" />
                                    {event.location.venue}
                                  </div>
                                </div>
                                

                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center mt-12">
                      <nav className="flex items-center space-x-2">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-4 py-2 rounded-md border ${
                              currentPage === page
                                ? 'bg-primary text-white border-primary'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        ))}
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRightIcon className="w-5 h-5" />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Events Found</h3>
                  <p className="text-gray-500">Try adjusting your filters to see more events.</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default EventsPage;